import React from 'react';

// Balanced confidence values - slow start, dramatic acceleration after 60%
// Lower values = manageable early game, but patterns still matter!
// Unlock values now represent EQUILIBRIUMS reached, not attempts
export const PIECE_CONFIG = {
  cube:    { confidence: 3,  color: '#18181B', label: 'Cube',       unlock: 0 },
  sphere:  { confidence: 2,  color: '#2563EB', label: 'Sphere',     unlock: 0 },
  pyramid: { confidence: 4,  color: '#10B981', label: 'Pyramid',    unlock: 0 },
  hexagon: { confidence: 2,  color: '#10B981', label: 'Hexagon',    unlock: 1 },  // 1 equilibrium - GREEN
  diamond: { confidence: 1,  color: '#F59E0B', label: 'Diamond',    unlock: 3 },  // 3 equilibriums
  rotate:  { confidence: 0,  color: '#0EA5E9', label: 'Rotate',     unlock: 5 },  // 5 equilibriums - rotates shapes
  pen:     { confidence: 0,  color: '#374151', label: 'Pen',        unlock: 7 },  // 7 equilibriums - draws lines only
  star:    { confidence: 6,  color: '#EC4899', label: 'Star',       unlock: 10 }, // 10 equilibriums - PINK
  void:    { confidence: -2, color: '#71717A', label: 'Void Block', unlock: 0 },  // Available from start
};

// Special shapes - won from equilibrium lottery (confidence -2, appear 1-3 times)
export const SPECIAL_SHAPES_CONFIG = {
  cross:    { confidence: -2, color: '#DC2626', label: 'Cross' },
  arrow:    { confidence: -2, color: '#0891B2', label: 'Arrow' },
  crescent: { confidence: -2, color: '#7C3AED', label: 'Crescent' },
  bolt:     { confidence: -2, color: '#FBBF24', label: 'Bolt' },
  wave:     { confidence: -2, color: '#06B6D4', label: 'Wave' },
  heart:    { confidence: -2, color: '#EC4899', label: 'Heart' },
  cloud:    { confidence: -2, color: '#94A3B8', label: 'Cloud' },
  spiral:   { confidence: -2, color: '#8B5CF6', label: 'Spiral' },
  fragment: { confidence: -2, color: '#64748B', label: 'Fragment' },
  eye:      { confidence: -2, color: '#14B8A6', label: 'Eye' },
};

export const SHAPE_COLORS = {
  ...Object.fromEntries(Object.entries(PIECE_CONFIG).map(([k, v]) => [k, v.color])),
  ...Object.fromEntries(Object.entries(SPECIAL_SHAPES_CONFIG).map(([k, v]) => [k, v.color])),
};

export function renderShape(type, cx, cy, size, colorOverride) {
  const half = size / 2 - 6;
  const color = colorOverride || SHAPE_COLORS[type] || '#18181B';

  switch (type) {
    case 'cube':
      return <rect x={cx - half} y={cy - half} width={half * 2} height={half * 2} fill={color} rx={3} />;
    case 'sphere':
      return <circle cx={cx} cy={cy} r={half} fill={color} />;
    case 'pyramid':
      return <polygon points={`${cx},${cy - half} ${cx - half},${cy + half} ${cx + half},${cy + half}`} fill={color} />;
    case 'hexagon': {
      const pts = Array.from({ length: 6 }, (_, i) => {
        const a = (Math.PI / 3) * i - Math.PI / 6;
        return `${cx + half * Math.cos(a)},${cy + half * Math.sin(a)}`;
      }).join(' ');
      return <polygon points={pts} fill={color} />;
    }
    case 'diamond':
      return <polygon points={`${cx},${cy - half} ${cx + half * 0.7},${cy} ${cx},${cy + half} ${cx - half * 0.7},${cy}`} fill={color} />;
    case 'star': {
      const outerR = half;
      const innerR = half * 0.4;
      const pts = Array.from({ length: 10 }, (_, i) => {
        const a = (Math.PI / 5) * i - Math.PI / 2;
        const r = i % 2 === 0 ? outerR : innerR;
        return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
      }).join(' ');
      return <polygon points={pts} fill={color} />;
    }
    case 'void':
      return <rect x={cx - half} y={cy - half} width={half * 2} height={half * 2} fill="none" stroke="#71717A" strokeWidth={2} strokeDasharray="4 4" rx={3} />;
    case 'rotate':
      // Rotate tool icon - circular arrow
      return (
        <g>
          <circle cx={cx} cy={cy} r={half * 0.8} fill="none" stroke={color} strokeWidth={2.5} strokeDasharray={`${half * 3.5} ${half * 1.5}`} />
          <polygon 
            points={`${cx + half * 0.5},${cy - half * 0.7} ${cx + half * 0.9},${cy - half * 0.5} ${cx + half * 0.5},${cy - half * 0.3}`} 
            fill={color} 
          />
        </g>
      );
    case 'pen':
      // Pen tool icon - simple line/pencil
      return (
        <g>
          <line x1={cx - half * 0.6} y1={cy + half * 0.6} x2={cx + half * 0.6} y2={cy - half * 0.6} stroke={color} strokeWidth={3} strokeLinecap="round" />
          <circle cx={cx - half * 0.6} cy={cy + half * 0.6} r={3} fill={color} />
        </g>
      );
    case 'chaos':
      return (
        <g>
          <rect x={cx - half} y={cy - half} width={half * 2} height={half * 2} fill="#EA580C" rx={3} opacity={0.85} />
          <line x1={cx - half / 2} y1={cy - half / 2} x2={cx + half / 2} y2={cy + half / 2} stroke="white" strokeWidth={2.5} />
          <line x1={cx + half / 2} y1={cy - half / 2} x2={cx - half / 2} y2={cy + half / 2} stroke="white" strokeWidth={2.5} />
        </g>
      );
    default:
      return null;
  }
}
